// =============================================================================
// TabFiles.jsx
// =============================================================================
// Invoices + supporting-documents upload step in the request wizard.
//
// Renders two side-by-side panels (or just the docs panel when showInvoices is
// false): Supporting Documents (6 slots) and Invoices (6 slots). Each slot
// uploads to Supabase Storage and back-fills the array in parent state.
//
// LEGACY SCHEMA ONLY — do not add new features here.
//
// Pulled out of RequestWizard.jsx as part of the wizard decomposition
// (May 2026).
//
// Props
// ─────
//   invoices     — array of invoice URLs (from form state)
//   setInvoices  — setter for invoices
//   docs         — array of doc URLs (from form state)
//   setDocs      — setter for docs
//   req          — the current request object (needs id, refNo, shelterCode,
//                  shelterName, state)
//   showInvoices — boolean: whether to render the invoice panel
//   user         — logged-in user (passed to HelpButton)
// =============================================================================
import React from "react";
import { getRequestFilesById } from "../../data/requests";
import { uploadFile, uploadInvoiceFile } from "../../data/storage";
import { alertDialog } from "../../services/dialogService";
import { validateUploadFile } from "../../services/fileTypeGuard";
import { createTask, sendEmail } from "../../utils/actions";
import { logger } from "../../utils/logger";
import { HelpButton } from "./ChildSections.jsx";
import { FileField } from "./FormUI.jsx";

export function TabFiles({ invoices, setInvoices, docs, setDocs, req, showInvoices, user }) {
  const [uploading, setUploading] = React.useState({});

  async function upd(arr, setArr, i, f, isInvoice) {
    if (!f) {
      const a = [...arr];
      a[i] = null;
      setArr(a);
      return;
    }
    // Content-aware type gate (PDF/JPG/PNG, extension AND magic bytes must agree).
    const valid = await validateUploadFile(f);
    if (!valid.ok) {
      await alertDialog({ message: valid.error });
      return;
    }
    // Upload to Supabase Storage
    var refNo = req?.refNo || "draft";
    var folder = isInvoice ? "invoices" : "docs";
    var safeName = `${Date.now()}_${f.name.replace(/[^a-zA-Z0-9._-]/g, "_")}`;
    var path = `${refNo}/${folder}/${safeName}`;
    var uploadKey = `${folder}_${i}`;
    setUploading(function (prev) {
      return Object.assign({}, prev, { [uploadKey]: true });
    });
    const uploadRes = isInvoice
      ? await uploadInvoiceFile(path, f, { upsert: true, contentType: valid.contentType })
      : await uploadFile(path, f, { upsert: true, contentType: valid.contentType });
    setUploading(function (prev) {
      return Object.assign({}, prev, { [uploadKey]: false });
    });
    if (uploadRes.error) {
      logger.warn("Upload failed:", uploadRes.error.message);
      return;
    }
    // Private buckets: invoices (db-26) and now all zephyr-files docs (db-168) store the
    // object PATH; rendering signs it on demand (getFileSignedUrl / getInvoiceSignedUrl).
    const stored = path;
    // Fetch fresh from DB to avoid overwriting docs from other sessions
    var reqId = req?.id;
    if (reqId) {
      var freshFiles2 = await getRequestFilesById(reqId);
      var freshField2 = isInvoice ? "invoices" : "docs";
      var freshArr2 = freshFiles2?.[freshField2] ? freshFiles2[freshField2] : [...arr];
      var insertIdx2 = i;
      while (insertIdx2 < freshArr2.length && freshArr2[insertIdx2]) insertIdx2++;
      if (insertIdx2 >= freshArr2.length) insertIdx2 = i;
      const a = freshArr2.slice();
      a[insertIdx2] = stored;
      setArr(a);
    } else {
      const a = [...arr];
      a[i] = stored;
      setArr(a);
    }
    // Send email notification.
    // Shelter actor: no user-facing warning shown (it's an internal notification
    // to the Zephyr team that the shelter can't act on). A send failure is still
    // recorded centrally in sendEmail (audit log) and alerts admin.
    if (isInvoice) {
      sendEmail("new_invoice", {
        requestId: req?.id,
        refNo: req?.refNo,
        shelterCode: req?.shelterCode || "",
        shelterName: req?.shelterName,
        fileName: f.name,
        // Invoices are private (db-26): the email links the request + shows the
        // file name; no direct file link.
      });
    } else {
      const currentRefNo = req?.refNo;
      const isSubmitted =
        currentRefNo && !String(currentRefNo).startsWith("DRAFT") && currentRefNo !== "Draft";
      if (isSubmitted) {
        createTask("new_file", {
          requestId: req?.id,
          refNo: currentRefNo,
          shelterName: req?.shelterName,
          state: req?.state,
          fileName: f.name,
        });
      }
    }
  }

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: showInvoices ? "1fr 1fr" : "1fr",
        gap: 16,
        alignItems: "start",
      }}
    >
      <div>
        <div
          style={{
            background: "#1565C0",
            color: "white",
            padding: "12px 20px",
            borderRadius: "10px 10px 0 0",
            fontWeight: 700,
            fontSize: 15,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>Supporting Documents</span>
          <span
            style={{
              fontWeight: 400,
              fontSize: 13,
              opacity: 0.9,
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            Upload guide
            <HelpButton helpKey="uploads" user={user} light={true} />
            <span style={{ marginLeft: 8 }}>
              Reimbursement
              <HelpButton helpKey="reimbursement" user={user} light={true} />
            </span>
          </span>
        </div>
        <div
          style={{
            border: "1px solid #CFE0DE",
            borderTop: "none",
            borderRadius: "0 0 10px 10px",
            padding: 16,
          }}
        >
          <p style={{ color: "#607D79", fontSize: 13, marginBottom: 14 }}>
            Upload any supporting documents relevant to this request.
          </p>
          {!showInvoices && (
            <div
              style={{
                background: "#FFF3E0",
                border: "1px solid #FFB300",
                borderRadius: 7,
                padding: "10px 14px",
                marginBottom: 14,
                fontSize: 17,
                color: "#E65100",
              }}
            >
              <span style={{ fontWeight: 700, textDecoration: "underline" }}>
                DO NOT UPLOAD DOCUMENTS THAT YOU HAVE ALREADY ATTACHED
              </span>{" "}
              during the initial stage of this request.
            </div>
          )}
          {!showInvoices && (
            <div
              style={{
                background: "#FFF3E0",
                border: "1px solid #FFB300",
                borderRadius: 7,
                padding: "10px 14px",
                marginBottom: 14,
                fontSize: 17,
                color: "#E65100",
              }}
            >
              <span style={{ fontWeight: 700, textDecoration: "underline" }}>
                INVOICES UPLOADED HERE WILL NOT BE DEALT WITH
              </span>
              . Once your Request has been submitted &amp; Approved, an Invoice upload area will
              appear on this page. Upload Invoices there.
            </div>
          )}
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <FileField
              key={i}
              label={`Document ${i + 1}`}
              file={docs[i]}
              onChange={(f) => upd(docs, setDocs, i, f, false)}
              uploading={uploading[`docs_${i}`]}
            />
          ))}
        </div>
      </div>
      {showInvoices && (
        <div>
          <div
            style={{
              background: "#00695C",
              color: "white",
              padding: "12px 20px",
              borderRadius: "10px 10px 0 0",
              fontWeight: 700,
              fontSize: 15,
            }}
          >
            Invoices &amp; Receipts
          </div>
          <div
            style={{
              border: "1px solid #CFE0DE",
              borderTop: "none",
              borderRadius: "0 0 10px 10px",
              padding: 16,
            }}
          >
            <p style={{ color: "#607D79", fontSize: 13, marginBottom: 14 }}>
              Invoice uploads trigger an automatic email to invoicing@zephyreducation.com.au.
            </p>
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <FileField
                key={i}
                label={`Invoice ${i + 1}`}
                file={invoices[i]}
                onChange={(f) => upd(invoices, setInvoices, i, f, true)}
                uploading={uploading[`invoices_${i}`]}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
